import { useState, useEffect } from 'react';
import type { Shift } from '../../../api/shifts.api';
import { validateAdmin } from '../../../api/auth.api';
import axiosInstance from '../../../api/axios';
import ShiftClosureWizard from './ShiftClosureWizard';
import { CurrencyInput } from '../../../utils/currencyFormat';

interface ShiftModalsProps {
    isOpenModalOpen: boolean;
    isCloseModalOpen: boolean;
    onClose: () => void;
    onOpenShift: (monto: number) => Promise<boolean>;
    onCloseShift: (data: any) => Promise<any>; 
    currentShift?: Shift | null;
    shiftId?: string; // Opcional: cuando admin cierra turno de otro usuario
    onCloseSuccess?: () => void; // Callback adicional después de cerrar
}

export default function ShiftModals({ 
    isOpenModalOpen, 
    isCloseModalOpen, 
    onClose, 
    onOpenShift,
    shiftId,
    onCloseSuccess
}: ShiftModalsProps) {
    const [montoBase, setMontoBase] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Auth State
    const [needsAuth, setNeedsAuth] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(false);
    
    // Auth Form State
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [authError, setAuthError] = useState('');
    const [isAuthorizing, setIsAuthorizing] = useState(false);

    useEffect(() => {
        if (isOpenModalOpen) {
            axiosInstance.get('/companies/settings')
                .then(res => {
                    const rule = res.data?.shiftAuthRule || 'SOLO_DESCUADRE';
                    if (rule === 'SIEMPRE' || rule === 'SOLO_ABRIR') {
                        setNeedsAuth(true);
                    } else {
                        setNeedsAuth(false);
                    }
                })
                .catch(err => console.error("Error fetching settings:", err));
        }
    }, [isOpenModalOpen]);

    const handleAdminAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');
        setIsAuthorizing(true);
        try {
            const response = await validateAdmin({ email: adminEmail, password: adminPassword });
            
            if (response && response.valid) {
                setIsAuthorized(true);
                // Reset form
                setAdminEmail('');
                setAdminPassword('');
            } else {
                setAuthError(response.message || 'Credenciales inválidas.');
            }
        } catch (err) {
            console.error('Auth error:', err);
            setAuthError('Error de conexión.');
        } finally {
            setIsAuthorizing(false);
        }
    };

    const handleOpenSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        const success = await onOpenShift(Number(montoBase));
        setIsSubmitting(false);
        if (success) {
            onClose();
            window.location.reload(); 
        }
    };

    if (isOpenModalOpen) {
        // Show Auth Form if needed and not yet authorized
        if (needsAuth && !isAuthorized) {
            return (
                 <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 animate-scaleIn">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto text-red-600 mb-3">
                                <i className="ri-shield-keyhole-line text-3xl"></i>
                            </div>
                            <h3 className="text-lg font-bold text-slate-800">Autorización Requerida</h3>
                            <p className="text-sm text-slate-500">
                                Se requiere autorización para abrir el turno.
                            </p>
                        </div>

                        <form onSubmit={handleAdminAuth} className="space-y-4">
                            {authError && (
                                <div className="bg-red-50 text-red-600 text-xs p-3 rounded-lg border border-red-100">
                                    {authError}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Email Administrador</label>
                                <input
                                    type="email"
                                    required
                                    value={adminEmail}
                                    onChange={e => setAdminEmail(e.target.value)}
                                    disabled={isAuthorizing}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg outline-none focus:border-teal-500 disabled:bg-slate-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Contraseña</label>
                                <input
                                    type="password"
                                    required
                                    value={adminPassword}
                                    onChange={e => setAdminPassword(e.target.value)}
                                    disabled={isAuthorizing}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg outline-none focus:border-teal-500 disabled:bg-slate-50"
                                />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={isAuthorizing}
                                    className="px-4 py-2 rounded-lg font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isAuthorizing}
                                    className="flex-1 bg-red-600 text-white py-2 rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isAuthorizing ? (
                                        <>
                                            <i className="ri-loader-4-line animate-spin"></i>
                                            Autorizando...
                                        </>
                                    ) : (
                                        'Autorizar'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                 </div>
            );
        }

        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
                <div className="bg-white rounded-2xl p-0 w-full max-w-sm shadow-2xl overflow-hidden animate-scaleIn">
                    {/* Header */}
                    <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center">
                                <i className="ri-store-2-line text-lg"></i>
                            </div>
                            Apertura de Caja
                        </h2>
                        <button 
                            onClick={onClose}
                            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                        >
                            <i className="ri-close-line text-xl"></i>
                        </button>
                    </div>

                    <div className="p-6">
                        <form onSubmit={handleOpenSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-600 mb-2">
                                    Base Inicial en Caja
                                </label>
                                <CurrencyInput
                                    value={montoBase}
                                    onChange={setMontoBase}
                                    className="w-full px-4 py-4 border border-slate-200 rounded-xl text-2xl font-bold text-slate-800 outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                                    placeholder="$0"
                                    autoFocus
                                />
                                <p className="mt-2 text-xs text-slate-400 flex items-center gap-1">
                                    <i className="ri-information-line"></i>
                                    Ingrese el dinero en efectivo disponible al iniciar
                                </p>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting || montoBase < 0} 
                                    className="flex-1 bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-teal-600/20 hover:shadow-teal-600/30 transition-all flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <i className="ri-loader-4-line animate-spin"></i>
                                            Abriendo...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-check-line"></i>
                                            Abrir Turno
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    if (isCloseModalOpen) {
        return (
            <ShiftClosureWizard 
                onClose={onClose}
                onSuccess={() => {
                    // Close modal and reload app to refresh shift state everywhere
                    onClose();
                    if (onCloseSuccess) {
                        onCloseSuccess();
                    } else {
                        window.location.reload();
                    }
                }}
                shiftId={shiftId}
            />
        );
    }

    return null;
}
